/**
 * WallSkillEffect — Earth tree wall path (Fork 1) active skill.
 *
 * Instant activation — no wind-up. On activation, raises a 3-tile-wide stone
 * barrier perpendicular to the player's facing direction, 1 tile in front.
 * The wall has 80 HP and expires after 8 seconds if not destroyed first.
 *
 * Upgrade keys on scope 'wall':
 * - wallHp: additive, base 0 (Reinforced: +40)
 * - wallDurationMs: additive, base 0 (Reinforced: +3000ms)
 * - proximityDamageReduction: flag/value (Warded: 0.1, 10% DR in 1.5-tile radius)
 *
 * Warded interaction: if resolver.hasEffect('wall', 'proximityDamageReduction'),
 * the TerrainConfig is populated with proximityEffect: { radiusTiles: 1.5,
 * damageReduction: 0.1 }. TerrainConstructionSystem handles the DR proximity
 * check at the integration layer — WallSkillEffect only sets the config.
 *
 * Max 1 active wall: LRU eviction — if a wall is active when the player
 * activates again, the old wall is removed before the new one is placed.
 * Rampart (wall_evolved) lifts this cap to 2 active walls, but that behavior
 * belongs to RampartSkillEffect, not here.
 *
 * Placement position and footprint tiles are computed by the game layer and
 * passed through deps.placement.getWallPlacement(). PathfindingSystem receives
 * the 3-tile footprint synchronously via deps.pathfinding.markImpassable().
 *
 * Extends InstantSkillEffect; overrides cooldown and update for timer tracking.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Wall Path — Wall
 */

import { InstantSkillEffect } from '../../InstantSkillEffect.js';

// ---------------------------------------------------------------------------
// Terrain config interface (mirrors TerrainConfig subset for wall placements)
// ---------------------------------------------------------------------------

export interface WallTerrainConfig {
  readonly type: 'wall';
  readonly position: { x: number; y: number };
  readonly widthTiles: number;
  readonly heightTiles: number;
  readonly hp: number;
  readonly durationMs: number;
  readonly sourceSkillId: string;
  readonly blocksMovement: boolean;
  readonly blocksProjectiles: boolean;
  readonly proximityEffect?: {
    readonly radiusTiles: number;
    readonly damageReduction: number;
  };
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface WallDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly placement: {
    /**
     * Returns the wall's center position (1 tile in front of the player,
     * perpendicular to facing direction) and the 3 tile footprint positions
     * that the pathfinding grid must mark impassable.
     */
    getWallPlacement(): {
      position: { x: number; y: number };
      footprintTiles: Array<{ x: number; y: number }>;
    };
  };
  readonly terrain: {
    place(config: WallTerrainConfig): string;
    remove(terrainId: string): void;
  };
  readonly pathfinding: {
    /**
     * Marks the given world-space positions as impassable in the pathfinding
     * grid, synchronously on the same frame as wall placement.
     */
    markImpassable(positions: Array<{ x: number; y: number }>): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface WallConfig {
  /** Base HP of the placed wall before upgrades. */
  readonly baseHp: number;
  /** Base duration in ms before expiry. 0 = permanent. */
  readonly baseDurationMs: number;
  /** Cooldown in ms after each activation (medium tier). */
  readonly cooldownMs: number;
  /** Width of the wall in tiles. */
  readonly widthTiles: number;
  /** Height of the wall in tiles. */
  readonly heightTiles: number;
  /** Proximity DR radius in tiles (Warded upgrade). */
  readonly proximityRadiusTiles: number;
  /** Proximity DR value applied when Warded is unlocked. */
  readonly proximityDamageReduction: number;
}

const DEFAULT_CONFIG: WallConfig = {
  baseHp: 80,
  baseDurationMs: 8000,
  cooldownMs: 800,       // medium cooldown tier
  widthTiles: 3,
  heightTiles: 1,
  proximityRadiusTiles: 1.5,
  proximityDamageReduction: 0.1,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class WallSkillEffect extends InstantSkillEffect {
  readonly skillId = 'wall';

  private readonly deps: WallDeps;
  private readonly config: WallConfig;

  // Cooldown timer (ms remaining until ready)
  private _cooldownTimer = 0;

  // ID of the currently active wall (for LRU eviction). Null = no active wall.
  private _activeWallId: string | null = null;

  // Telemetry
  private _wallsPlacedCount = 0;
  private _pathfindingCallCount = 0;
  private _lastTerrainConfig: WallTerrainConfig | null = null;

  constructor(deps: WallDeps, config: Partial<WallConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  override get cooldown(): number { return this._cooldownTimer; }

  /** ID of the currently active wall, or null if none is placed. */
  get activeWallId(): string | null { return this._activeWallId; }

  get wallsPlacedCount(): number { return this._wallsPlacedCount; }

  get pathfindingCallCount(): number { return this._pathfindingCallCount; }

  /** The TerrainConfig used for the most recently placed wall. */
  get lastTerrainConfig(): WallTerrainConfig | null { return this._lastTerrainConfig; }

  // --- InstantSkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownTimer <= 0;
  }

  activate(): boolean {
    if (this._cooldownTimer > 0) return false;

    // LRU eviction: remove the existing wall before placing a new one
    if (this._activeWallId !== null) {
      this.deps.terrain.remove(this._activeWallId);
      this._activeWallId = null;
    }

    // Resolve HP and duration (base + upgrade additive delta)
    const bonusHp = this.deps.resolver.getAdditiveEffect('wall', 'wallHp');
    const bonusDurationMs = this.deps.resolver.getAdditiveEffect('wall', 'wallDurationMs');
    const effectiveHp = this.config.baseHp + bonusHp;
    const effectiveDurationMs = this.config.baseDurationMs + bonusDurationMs;

    // Warded: include proximityEffect if the upgrade key is active
    const hasWarded = this.deps.resolver.hasEffect('wall', 'proximityDamageReduction');
    const proximityEffect = hasWarded
      ? {
          radiusTiles: this.config.proximityRadiusTiles,
          damageReduction: this.config.proximityDamageReduction,
        }
      : undefined;

    // Compute placement position and footprint via game layer
    const { position, footprintTiles } = this.deps.placement.getWallPlacement();

    const terrainConfig: WallTerrainConfig = {
      type: 'wall',
      position,
      widthTiles: this.config.widthTiles,
      heightTiles: this.config.heightTiles,
      hp: effectiveHp,
      durationMs: effectiveDurationMs,
      blocksMovement: true,
      blocksProjectiles: true,
      sourceSkillId: 'wall',
      ...(proximityEffect !== undefined ? { proximityEffect } : {}),
    };

    const terrainId = this.deps.terrain.place(terrainConfig);
    this._activeWallId = terrainId;
    this._lastTerrainConfig = terrainConfig;
    this._wallsPlacedCount++;

    // Synchronous pathfinding update — footprint tiles are now impassable
    this.deps.pathfinding.markImpassable(footprintTiles);
    this._pathfindingCallCount++;

    // Start cooldown
    this._cooldownTimer = this.config.cooldownMs;

    return true;
  }

  override update(delta: number): void {
    if (this._cooldownTimer > 0) {
      this._cooldownTimer -= delta;
      if (this._cooldownTimer < 0) this._cooldownTimer = 0;
    }
  }

  /**
   * Returns the Warded proximity damage reduction for the given player position.
   *
   * Called per-hit by CombatHitResolver (or TerrainConstructionSystem). Returns
   * 0.1 (10% DR) when Warded is unlocked and the player is within 1.5 tiles of
   * the active wall; returns 0 otherwise. Non-stacking — one wall contribution
   * maximum regardless of wall count.
   *
   * @param playerX - Player world X position (pixels).
   * @param playerY - Player world Y position (pixels).
   * @param tileSize - Pixels per tile (typically 32).
   */
  getProximityDamageReduction(playerX: number, playerY: number, tileSize: number): number {
    if (this._activeWallId === null) return 0;
    if (this._lastTerrainConfig?.proximityEffect === undefined) return 0;

    const { position, proximityEffect } = this._lastTerrainConfig;
    const dist = Math.hypot(playerX - position.x, playerY - position.y);

    return dist <= proximityEffect.radiusTiles * tileSize
      ? proximityEffect.damageReduction
      : 0;
  }
}
